import {useLingui} from '@lingui/react/macro';
import {Svg, Path} from 'react-native-svg';
import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useWindowDimensions, StyleSheet, View, Text, Pressable} from 'react-native';
import {useNavigate} from 'extensions/navigation';
import {useModalBulkDownload} from 'interface/hooks/useModalBulkDownload';
import {useModalShare} from 'interface/hooks/useModalShare';
import {useModalUpsellFeature} from 'interface/hooks/useModalUpsellFeature';
import {vectors, colors} from 'config/styles';
import {theme} from 'config/themes';
import {selectors} from 'store/slices/files';

import * as app from 'store/slices/app';
import * as effects from 'interface/hooks/effects/files';

export interface FolderHeaderProps {
  id: string;
}

export function FolderHeader(props: FolderHeaderProps) {
  const {t} = useLingui();
  const screen = useWindowDimensions();
  const isPremium = useSelector(app.selectors.isPremium);
  const isLoggedIn = useSelector(app.selectors.isLoggedIn);
  const folder = useSelector(selectors.getItem(props.id));
  const depth = folder ? folder.hierarchy && folder.hierarchy.length : 0;
  const name = folder ? folder.name : '';
  const owner = folder ? folder.owner : '';
  const url = folder ? folder.url : undefined;
  const isLarge = screen.width > 800;
  const hasPrivate = folder ? folder.private : false;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const goBack = useCallback(() => navigate(-1), []);
  const login = useCallback(() => navigate('/login'), []);
  const save = useCallback(effects.pick(dispatch, {
    destination: 'myfiles',
    action: 'save',
    ids: [props.id],
  }), [props.id, dispatch]);
  
  const [showShare] = useModalShare([props.id], name, url, undefined, hasPrivate);
  const modalBulkDownload = useModalBulkDownload([props.id]);
  const [showUpsellBulkDownload] = useModalUpsellFeature('pro', 'bulkdownload');

  return (
    <View style={styles.root}>
      <View style={styles.titleContainer}>
        {depth > 0 &&
          <View style={styles.back}>
            <Pressable onPress={goBack}>
              <Svg style={styles.icon} viewBox={'0 0 20 20'} width={20} height={20}>
                <Path d={vectors.icons.back} fill={colors.primary.brand}/>
              </Svg>
            </Pressable>
          </View>
        }
        <Text style={styles.title} numberOfLines={1} ellipsizeMode="tail">
          {name}
        </Text>
      </View>
      
      {owner && owner !== ' ' &&
        <View style={styles.ownerContainer}>
          <Text style={styles.owner}>
            {t`shared by ${owner || '...'}`}
          </Text>
        </View>
      }
      
      <View style={styles.spacer}></View>
      
      <View style={styles.actions}>
        <View style={styles.button}>
          <Pressable 
            onPress={isPremium ? modalBulkDownload.open : showUpsellBulkDownload} 
            style={styles.buttonContent}>
            {(e) => <>
              <View style={styles.iconContainer}>
                <Svg viewBox={'0 0 16 16'} width={18} height={19}>
                  <Path d={vectors.icons.download} fill={e.hovered ? theme.dialog.text : theme.files.breadcrumbs.text}/>
                </Svg>
              </View>
              {isLarge &&
                <Text style={[styles.action, {color: e.hovered ? theme.dialog.text : theme.files.breadcrumbs.text}]}>
                  {t`Download`.toUpperCase()}
                </Text>
              }
            </>
          }
          </Pressable>
        </View>
        <View style={styles.button}>
          <Pressable onPress={showShare} style={styles.buttonContent}>
            {(e) => <>
              <View style={styles.iconContainer}>
                <Svg viewBox={'0 0 20 20'} width={18} height={16}>
                  <Path d={vectors.icons.share} fill={e.hovered ? theme.dialog.text : theme.files.breadcrumbs.text}/>
                </Svg>
              </View>
              {isLarge &&
                <Text style={[styles.action, {color: e.hovered ? theme.dialog.text : theme.files.breadcrumbs.text}]}>
                  {t`Share`.toUpperCase()}
                </Text>
              }
            </>
          }
          </Pressable>
        </View>
        <View style={styles.button}>
          <Pressable onPress={isLoggedIn ? save : login} style={styles.buttonContent}>
            {(e) => <>
              <View style={styles.iconContainer}>
                <Svg viewBox={'0 0 60 48'} width={28} height={22}>
                  <Path
                    d={vectors.icons.save}
                    fill={e.hovered ? theme.dialog.text : theme.files.breadcrumbs.text}
                  />
                </Svg>
              </View>
              {isLarge &&
                <Text style={[styles.action, {color: e.hovered ? theme.dialog.text : theme.files.breadcrumbs.text}]}>
                  {t`Save to My Files`.toUpperCase()}
                </Text>
              }
          </>}
          </Pressable>
        </View>
      </View>
    </View>
  );
}

export const styles = StyleSheet.create({
  root: {
    flexDirection: 'row',
    height: 46,
    margin: 14,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: '70%',
  },
  large: {
    height: 72,
    marginHorizontal: 0,
  },
  title: {
    lineHeight: 46,
    fontSize: 24,
    color: theme.dialog.text,
    paddingHorizontal: 8,
    flexShrink: 1,
  },
  icon: {
    marginLeft: 8,
  },
  back: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 8,
  },
  ownerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 8,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 12,
  },
  spacer: {
    flex: 1,
  },
  button: {
    marginLeft: 12,
    paddingHorizontal: 8,
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  action: {
    color: theme.files.breadcrumbs.text,
    fontSize: 11,
    lineHeight: 46,
    marginLeft: 8,
  },
  owner: {
    fontSize: 14,
    color: theme.files.breadcrumbs.text,
  },
});
